import { useEffect, useState, useRef, useContext, useMemo, useLayoutEffect } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { getMyAccountId, isBMI } from "../utils/config";
import { FaShoppingCart } from "react-icons/fa";
import { CartContext } from "../context/CartContext";
import { addToCartAndOrderExpress } from "../utils/addToCartAndOrderExpress";
import { API_BASE } from "../utils/api";
import { Link } from "react-router-dom";
import { hasAccountAccess } from "../utils/permissions";
import Unauthorized from "./Unauthorized";
import { useLanguage } from "../context/LanguageContext";
import ProductCard from "../components/FrequentProducts/ProductCard";
import Pagination from "../components/UI/Pagination";

// Ajustado a 30 para una paginación más manejable visualmente
const PAGE_SIZE = 30;

export default function CategoryPage() {
    const { categoryId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const user = JSON.parse(localStorage.getItem("user"));
    const { addToCart } = useContext(CartContext);
    const MYACCOUNT_ID = getMyAccountId();

    const [categories, setCategories] = useState([]);
    const [groupsByCategory, setGroupsByCategory] = useState({});
    const [activeCategory, setActiveCategory] = useState(null);
    const [selectedGroups, setSelectedGroups] = useState(null);

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return Boolean(params.get("search"));
    });
    const [searchText, setSearchText] = useState("");
    const { t, switchLanguage, language } = useLanguage();

    // ESTADOS DE PAGINACIÓN
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(10);

    const [hasProdFreq, setHasProdFreq] = useState(false);
    
    // Refs para controlar la restauración
    const isInitialMount = useRef(true);
    const isRestored = useRef(false);
    const navigationRestored = useRef(false);

    // Restaurar estado desde sessionStorage al montar el componente
    useLayoutEffect(() => {
        // Si la URL tiene ?search=, bloquear fetchProductsByPage desde el inicio
        const hasSearchParam = Boolean(new URLSearchParams(window.location.search).get("search"));
        if (hasSearchParam) setIsSearching(true);

        const savedGroup = sessionStorage.getItem("last_selected_group");
        const savedPage = sessionStorage.getItem("last_category_page");
        const savedFreq = sessionStorage.getItem("last_has_prod_freq");
        const savedCategoryId = sessionStorage.getItem("last_category_id");

        // Si hay un categoryId guardado y es diferente al actual, navegar a él
        // — pero NO si hay un ?search= en la URL (búsqueda nueva desde otra página),
        // y SOLO en el primer montaje: si no, cualquier click nuevo (navbar o
        // sidebar) que cambie de categoría se revertía de inmediato al valor viejo.
        if (isInitialMount.current && !hasSearchParam && savedCategoryId && savedCategoryId !== "null" && savedCategoryId !== "undefined" && savedCategoryId !== categoryId && !navigationRestored.current) {
            navigationRestored.current = true;
            history.push(`/categoria/${savedCategoryId}`);
            return; // Salir para que la navegación ocurra antes de restaurar otros valores
        }

        if (isInitialMount.current) {
            // Montaje real (recarga de pagina, o llegada desde otra pagina): restaurar
            // el ultimo estado guardado en sessionStorage.

            // Restaurar grupo - mantener como string
            if (savedGroup && savedGroup !== "null" && savedGroup !== "undefined" && savedGroup !== "") {
                // Si el grupo guardado tiene comillas (por JSON.stringify), limpiarlas
                let cleanGroup = savedGroup;
                if (savedGroup.startsWith('"') && savedGroup.endsWith('"')) {
                    cleanGroup = savedGroup.slice(1, -1);
                }
                setSelectedGroups(cleanGroup);
             //   console.log("Grupo restaurado:", cleanGroup);
            } else {
                setSelectedGroups(null);
            }

            // Restaurar página
            if (savedPage && savedPage !== "undefined" && savedPage !== "1") {
                setCurrentPage(Number(savedPage));
           //     console.log("Página restaurada:", savedPage);
            }

            // Restaurar filtro de frecuentes
            if (savedFreq !== null && savedFreq !== "undefined") {
                setHasProdFreq(savedFreq === "true");
           //     console.log("Filtro frecuentes restaurado:", savedFreq);
            }
        } else {
            // Cambio de categoría durante la sesión, venga de donde venga (sidebar,
            // barra superior, etc.): el grupo/subcategoria y la pagina son
            // especificos de la categoria anterior, asi que hay que limpiarlos aqui
            // de forma centralizada. Antes solo el sidebar lo hacia manualmente en su
            // onClick, por lo que entrar por la barra superior (Link simple, sin
            // reset) dejaba pegado un group_id que no existe en la nueva categoria y
            // la busqueda de productos devolvia vacio.
            setSelectedGroups(null);
            setCurrentPage(1);
        }

        isRestored.current = true;
    }, [history, categoryId]);

    // Cargar categorías
    useEffect(() => {
        let url = `${API_BASE}/inventary-ultra/api/ws/list_categorys?myaccount_id=${MYACCOUNT_ID}`;
        if (isBMI(user)) url += `&id_customer=${user.idCustomer}`;
        fetch(url)
            .then((res) => res.json())
            .then(setCategories)
            .catch((err) => console.error("Error cargando categorías", err));
    }, []);

    // Cargar grupos por categoría
    useEffect(() => {
        if (!categories.length) return;
        categories.forEach((cat) => {
            let url = `${API_BASE}/inventary-ultra/api/ws/list_groups?myaccount_id=${MYACCOUNT_ID}&category_id=${cat.categoryId}`;
            if (isBMI(user)) url += `&id_customer=${user.idCustomer}`;
            fetch(url)
                .then((res) => res.json())
                .then((data) => {
                    setGroupsByCategory((prev) => ({ ...prev, [cat.categoryId]: data }));
                })
                .catch(() => console.error("Error cargando grupos", cat.categoryId));
        });
    }, [categories]);

    // Búsqueda via query param ?search= — se dispara al montar y al regresar con goBack()
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const term = params.get("search");
        if (term) {
            searchProducts(term);
        }
    }, [location.search]);

    const toggleFrequent = (e) => {
        setHasProdFreq(e.target.checked);
        setCurrentPage(1);
    };

    const searchProducts = async (search) => {
        if (!search.trim()) return;
        setLoading(true);
        setIsSearching(true);
        setSearchText(search);
        try {
            let url = `${API_BASE}/inventary-ultra/api/ws/search_products_api?myaccount_id=${MYACCOUNT_ID}&search=${encodeURIComponent(search)}&solo_acuerdo=${isBMI(user)}`;
            if (user?.idCustomer) {
                url += `&bodega_id=${user.bodegaId}&id_customer=${user.idCustomer}&id_ship_to=${user.idShipTo ?? 0}`;
            }
            const res = await fetch(url);
            const data = await res.json();
            setProducts(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error buscando productos", err);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // Función de carga por página
    const fetchProductsByPage = async (page) => {
        if (isSearching) return;
        setLoading(true);

        try {
            const currentOffset = (page - 1) * PAGE_SIZE;
            const params = new URLSearchParams({
                myaccount_id: MYACCOUNT_ID,
                bodega_id: user && user.bodegaId ? user.bodegaId : "  ",
                has_prod_freq: hasProdFreq,
                solo_acuerdo: isBMI(user),
                limit: PAGE_SIZE,
                offset: currentOffset
            });

            if (categoryId && categoryId !== "all") params.append("category_id", categoryId);
            if (selectedGroups) params.append("group_id", selectedGroups);

            if (user?.idCustomer) {
                params.append("id_customer", user.idCustomer);
                params.append("id_ship_to", user.idShipTo ?? 0);
            }

            const res = await fetch(`${API_BASE}/inventary-ultra/api/ws/listProducts?${params.toString()}`);
            const data = await res.json();

            const newProducts = data?.products || [];
            const total = data?.totalRecords || 0;

            setProducts(newProducts);
            const calculatedTotal = Math.ceil(total / PAGE_SIZE);
            setTotalPages(calculatedTotal > 0 ? calculatedTotal : 1);

        } catch (e) {
            console.error("Error en fetchProductsByPage:", e);
            setProducts([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Guardar categoría en sessionStorage
    useEffect(() => {
        if (!isInitialMount.current && isRestored.current && categoryId) {
            sessionStorage.setItem("last_category_id", categoryId);
        //    console.log("Guardando categoría:", categoryId);
        }
    }, [categoryId]);

    // Guardar página en sessionStorage
    useEffect(() => {
        if (!isInitialMount.current && isRestored.current) {
            sessionStorage.setItem("last_category_page", currentPage);
            console.log("Guardando página:", currentPage);
        }
    }, [currentPage]);

    // Guardar grupo en sessionStorage
    useEffect(() => {
        if (!isInitialMount.current && isRestored.current) {
            // Guardar como string directamente, si es null guardar string vacío
            sessionStorage.setItem("last_selected_group", selectedGroups || "");
            console.log("Guardando grupo:", selectedGroups);
        }
    }, [selectedGroups]);

    // Guardar filtro de frecuentes en sessionStorage
    useEffect(() => {
        if (!isInitialMount.current && isRestored.current) {
            sessionStorage.setItem("last_has_prod_freq", hasProdFreq);
            console.log("Guardando filtro frecuentes:", hasProdFreq);
        }
    }, [hasProdFreq]);

    // Restaurar scroll después de cargar productos
    useEffect(() => {
        if (!loading && products.length > 0 && isRestored.current) {
            const savedScroll = sessionStorage.getItem("product_list_scroll");
            if (savedScroll && savedScroll !== "null" && savedScroll !== "undefined") {
                setTimeout(() => {
                    window.scrollTo({ top: Number(savedScroll), behavior: 'auto' });
                    sessionStorage.removeItem("product_list_scroll");
                }, 100);
            }
        }
    }, [loading, products]);

    // Cargar productos cuando cambien las dependencias
    useEffect(() => {
        // Si en la URL actual hay un ?search=, hay una busqueda en curso (la
        // maneja el efecto de "Busqueda via query param" mas abajo). No pisarla
        // con el listado generico: el estado "isSearching" que usa
        // fetchProductsByPage se actualiza en un useLayoutEffect aparte y, justo
        // al navegar a /categoria/x?search=..., categoryId cambia ANTES de que
        // ese estado se asiente, asi que el guard de fetchProductsByPage no
        // alcanza a frenarlo — se disparaban las dos peticiones en carrera y la
        // del listado generico a veces ganaba, mostrando resultados sin filtrar.
        const hasSearchParam = Boolean(new URLSearchParams(location.search).get("search"));
        if (hasSearchParam) {
            isInitialMount.current = false;
            return;
        }

        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (isRestored.current) {
                fetchProductsByPage(currentPage);
            }
        } else {
            fetchProductsByPage(currentPage);
        }
    }, [currentPage, categoryId, selectedGroups, hasProdFreq]);

    // Actualizar categoría activa
    useLayoutEffect(() => {
        if (categoryId) {
            setActiveCategory(categoryId);
        }
    }, [categoryId]);

    const toggleGroup = (groupId) => {
        setSelectedGroups((prev) => prev === groupId ? null : groupId);
        setCurrentPage(1);
    };

    return (
        <div className="pt-28 px-2 lg:px-4 flex gap-4 w-full">
            <aside className="hidden lg:block w-64 bg-[#FAF9F7] border border-gray-200 shadow-sm p-1 rounded-2xl sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <div className="flex items-center justify-between px-0 mb-3">
                    <Link to="/categoria/all" onClick={() => {
                        setIsSearching(false);
                        setSearchText("");
                        setSelectedGroups(null);
                        setActiveCategory(null);
                        setCurrentPage(1);
                        sessionStorage.removeItem("last_selected_group");
                        sessionStorage.removeItem("last_category_page");
                        sessionStorage.removeItem("last_category_id");
                        sessionStorage.removeItem("last_has_prod_freq");
                        sessionStorage.removeItem("product_list_scroll");
                        navigationRestored.current = false;
                    }}>
                        <h3 className="text-[14px] font-bold cursor-pointer transition-colors hover:text-brand-primary uppercase tracking-wider">
                            Categorias
                        </h3>
                    </Link>

                    {user && (
                        <div className="relative flex items-center group/tooltip">
                            <input
                                type="checkbox"
                                checked={hasProdFreq}
                                onChange={toggleFrequent}
                                className="cursor-pointer w-4 h-4 appearance-auto accent-[var(--color-primary)] outline-none focus:outline-none focus:ring-0"
                            />
                            <div className="absolute top-full right-0 mt-2 hidden group-hover/tooltip:block w-[140px] bg-gray-800 text-white text-[10px] py-1 px-1 rounded shadow-2xl z-[9999] text-center">
                                {hasProdFreq ? "Mostrar todos los productos" : "Solo productos frecuentes"}
                            </div>
                        </div>
                    )}
                </div>

                <ul className="space-y-4">
                    {categories.map((cat) => {
                        const isOpen = String(activeCategory) === String(cat.categoryId);
                        return (
                            <li key={cat.categoryId}>
                                <button
                                    onClick={() => {
                                        setIsSearching(false);
                                        setSearchText("");
                                        setCurrentPage(1);
                                        setSelectedGroups(null);
                                        setActiveCategory(isOpen ? null : cat.categoryId);
                                        history.push(`/categoria/${cat.categoryId}`);
                                    }}
                                    className={`w-full flex justify-between items-center text-[12px] uppercase -mt-2
                                    transition-all ${isOpen ? "font-bold text-black" : "font-semibold text-gray-700 hover:text-brand-primary"}`}>
                                    <span>{cat.categoryName}</span>
                                </button>

                                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"}`}>
                                    <ul className="space-y-1 mt-1">
                                        <li>
                                            <div
                                                className={`flex items-center justify-between px-2 py-0.5 cursor-pointer text-[11px] rounded transition-colors ${selectedGroups === null && String(categoryId) === String(cat.categoryId) ? "bg-gray-100 font-bold" : "hover:bg-gray-50 text-gray-700"}`}
                                                onClick={() => {
                                                    if (String(categoryId) !== String(cat.categoryId)) {
                                                        setIsSearching(false);
                                                        setSearchText("");
                                                        history.push(`/categoria/${cat.categoryId}`);
                                                    } else {
                                                        setIsSearching(false);
                                                        setSearchText("");
                                                    }
                                                    setSelectedGroups(null);
                                                }}
                                            >
                                                <span className="truncate">{t.allProducts}</span>

                                                {selectedGroups === null && String(categoryId) === String(cat.categoryId) && user && (
                                                    <div className="relative flex items-center group/tooltip">
                                                        <input
                                                            type="checkbox"
                                                            checked={hasProdFreq}
                                                            onChange={toggleFrequent}
                                                            className="w-3.5 h-3.5 appearance-auto accent-[var(--color-primary)] outline-none focus:outline-none focus:ring-0"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <div className="absolute top-full right-0 mt-2 hidden group-hover/tooltip:block w-max bg-gray-800 text-white text-[9px] py-1 px-2 rounded shadow-xl z-50 pointer-events-none">
                                                            {hasProdFreq ? "Mostrar todos los productos" : "Solo productos frecuentes"}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </li>

                                        {(groupsByCategory[cat.categoryId] || []).map((g) => {
                                            const isSelected = selectedGroups === g.groupId;
                                            return (
                                                <li key={g.groupId}>
                                                    <div className={`flex items-center justify-between px-2 py-0.5 cursor-pointer text-[11px] rounded ${isSelected ? "bg-gray-100 font-bold" : "hover:bg-gray-50 text-gray-700"}`}
                                                        onClick={() => toggleGroup(g.groupId)}>
                                                        <span className="truncate">{g.groupName}</span>
                                                        {isSelected && user && (
                                                            <div className="relative flex items-center group/tooltip">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={hasProdFreq}
                                                                    onChange={toggleFrequent}
                                                                    className="w-3.5 h-3.5 appearance-auto accent-[var(--color-primary)] outline-none focus:outline-none focus:ring-0"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                                <div className="absolute bottom-full right-0 mb-2 hidden group-hover/tooltip:block w-max bg-gray-800 text-white text-[9px] py-1 px-2 rounded shadow-xl z-50 pointer-events-none">
                                                                    {hasProdFreq ? "Mostrar todos los productos" : "Solo productos frecuentes"}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </aside>

            <main className="flex-1 px-1 flex flex-col">
                {!isSearching && products.length > 0 && (
                    <div className="-mt-0 -mb-2 pt-0">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                    {products.map((p) => (
                        <ProductCard
                            key={`${p.productId}-${isSearching}`}
                            product={p}
                            t={t}
                            fontSize="text-[11px]"
                            px="px-0"
                            highlighted={true}
                            categoryId={categoryId}
                            selectedGroups={selectedGroups}
                            currentPage={currentPage}
                            hasProdFreq={hasProdFreq}
                        />
                    ))}
                </div>

                {!isSearching && products.length > 0 && (
                    <div className="mt-8 mb-0 pt-0">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}

                {loading && <p className="text-center mt-6">Cargando productos...</p>}
                {!loading && products.length === 0 && <p className="text-center mt-6 text-3xl italic text-red-600">No se encontraron productos</p>}
            </main>
        </div>
    );
}